import { Link } from "react-router-dom";
import { ChevronRight, Heart, Eye } from "lucide-react";
import { useState } from "react";
import { useLocale } from "../contexts/LocaleContext";
import { useWishlist } from "../contexts/WishlistContext";
import QuickViewModal from "./QuickViewModal";

export default function ProductSection({ overline, title, items, viewAll = "/shop", id }) {
  const { fmt } = useLocale();
  const { has, toggle } = useWishlist();
  const [qvId, setQvId] = useState(null);
  if (!items || items.length === 0) return null;
  return (
    <section id={id} className="max-w-[1500px] mx-auto px-6 md:px-12 py-20 md:py-24">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="overline mb-3">{overline}</div>
          <h2 className="font-display text-4xl md:text-5xl text-dark tracking-tight">{title}</h2>
        </div>
        <Link to={viewAll} data-testid={`view-all-${id||""}`} className="text-sm uppercase tracking-luxe text-gray-600 hover:text-copper flex items-center gap-1">View all <ChevronRight size={14}/></Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-5 gap-y-12">
        {items.slice(0, 6).map(p => (
          <div key={p.id} className="group relative">
            <Link to={`/product/${p.id}`} data-testid={`section-${id||""}-prod-${p.id}`} className="product-card block">
              <div className="aspect-square overflow-hidden bg-[#F5F2ED] relative">
                <img src={p.images?.[0]} alt={p.name} loading="lazy" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=900"; }} className="w-full h-full object-cover"/>
                {p.is_new && <span className="absolute top-3 left-3 bg-copper text-white text-[9px] font-semibold uppercase tracking-luxe px-2 py-1">New</span>}
                {p.original_price && p.original_price > p.price && (
                  <span className="absolute top-3 right-12 bg-red-600 text-white text-[10px] font-semibold px-2 py-1">−{Math.round((1 - p.price/p.original_price)*100)}%</span>
                )}
                {/* Quick view overlay button - shows on hover */}
                <button onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setQvId(p.id); }} data-testid={`qv-${p.id}`}
                        className="absolute bottom-3 left-3 right-3 bg-white text-dark text-[10px] uppercase tracking-luxe py-2 flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-dark hover:text-white">
                  <Eye size={12}/> Quick View
                </button>
              </div>
              <div className="pt-4">
                <div className="text-[10px] uppercase tracking-luxe text-copper">{p.brand}</div>
                <div className="text-sm text-dark mt-1.5 leading-tight line-clamp-2">{p.name}</div>
                <div className="text-sm mt-2">
                  {p.original_price && p.original_price > p.price ? (
                    <>
                      <span className="text-dark font-semibold">{fmt(p.price)}</span>
                      <span className="text-gray-400 line-through ml-2 text-xs">{fmt(p.original_price)}</span>
                    </>
                  ) : (
                    <span className="text-gray-700">{fmt(p.price)}</span>
                  )}
                </div>
              </div>
            </Link>
            <button onClick={() => toggle(p)} aria-label="Wishlist" className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white border border-black/5 transition-colors ${has(p.id)?"text-red-500":"text-gray-500 hover:text-red-500"}`}>
              <Heart size={14} fill={has(p.id)?"currentColor":"none"}/>
            </button>
          </div>
        ))}
      </div>
      <QuickViewModal productId={qvId} onClose={()=>setQvId(null)}/>
    </section>
  );
}
